import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Select,
  Button,
  Input,
  Spin,
  DatePicker,
  Cascader,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import map from 'lodash/map';
import last from 'lodash/last';
import filter from 'lodash/filter';
import find from 'lodash/find';
import includes from 'lodash/includes';
import { userMangement } from '../../../redux/actions/authActions';
import { offeringManagement } from '../../../redux/actions/offeringAction';
import { campaignManagement } from '../../../redux/actions/campaignAction';
import {
  getRetailerLocation,
  getLocationTouchPoints,
} from '../../../utilities/campaign';
import { hasAnyPermission } from '../../../utilities/helpers';
import { ROLE_ADMIN, ROLE_MANAGER } from '../../../constants/roles';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { SHOW_CHILD } = Cascader;

const CampaignDetails = (props) => {
  const { initialValues, onMove, salesChannel, onSubmitPayload } = props;
  const role = useSelector((state) => get(state, 'auth.role'));
  const isBrandUser = hasAnyPermission(
    [ROLE_ADMIN, ROLE_MANAGER],
    [get(role, 'role_code')]
  );

  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [retailers, setRetailers] = useState({
    list: [],
    selectedRetailers: [],
  });

  const [touchPoints, setTouchPoints] = useState({
    cities: [],
    points: [],
  });
  const [form] = Form.useForm();

  const onFinish = (values) => {
    const payload = {
      ...values,
      valid_from: get(values, 'time_range.0').format('YYYY-MM-DD'),
      valid_to: get(values, 'time_range.1').format('YYYY-MM-DD'),
      meta: {
        company: find(brands, ['id', get(values, 'company_id')]),
        offering: find(offerings, ['id', get(values, 'offering_id')]),
        valid_from: get(values, 'time_range.0').format('DD-MM-YYYY'),
        valid_to: get(values, 'time_range.1').format('DD-MM-YYYY'),
        cities: get(touchPoints, 'cities'),
      },
    };
    onSubmitPayload(1, payload);
  };

  const fetchBrands = () => {
    setLoading(true);
    dispatch(userMangement('list.brands'))
      .then((result) => {
        setLoading(false);
        setBrands(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchOfferings = (companyId, campaigntype) => {
    if (companyId && campaigntype) {
      setLoading(true);
      const filters = {
        type: campaigntype,
        sales_channel_id: [get(salesChannel, 'id')],
        company_id: isBrandUser ? undefined : [companyId],
      };

      dispatch(
        offeringManagement('list.offerings', {
          company_id: isBrandUser ? companyId : undefined,
          filters,
        })
      )
        .then((result) => {
          setLoading(false);
          if (!initialValues) {
            form.setFieldsValue({
              offering_id: undefined,
            });
          }
          setOfferings(get(result, 'data'));
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const fetchRetailers = () => {
    setLoading(true);
    dispatch(campaignManagement('list.retailers.cities'))
      .then((result) => {
        setLoading(false);
        setRetailers({
          list: get(result, 'data'),
          selectedRetailers: initialValues
            ? filter(get(result, 'data'), (retailer) =>
                includes(get(initialValues, 'retailer'), get(retailer, 'id'))
              )
            : [],
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchTouchPoints = (cities = [], editMode = false) => {
    if (cities.length > 0) {
      setLoading(true);
      dispatch(
        campaignManagement('list.touchpoints_by_cities', {
          touchpoint_city_ids: map(cities, 'value'),
        })
      )
        .then((result) => {
          setLoading(false);
          if (!editMode) {
            form.setFieldsValue({
              touchpoint_ids: undefined,
            });
          }
          setTouchPoints({
            points: get(result, 'data'),
            cities,
          });
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchRetailers();

    if (initialValues) {
      fetchOfferings(
        get(initialValues, 'company_id'),
        get(initialValues, 'type')
      );

      fetchTouchPoints(get(initialValues, 'meta.cities'), true);
    }
  }, []);

  useEffect(() => {
    if (isBrandUser) {
      form.setFieldsValue({
        company_id: get(selectedCompany, 'id'),
      });
    }
  }, [selectedCompany]);

  return (
    <Spin spinning={loading}>
      <Form
        name="basic"
        layout="vertical"
        form={form}
        initialValues={initialValues || {}}
        onFinish={onFinish}
        autoComplete="off"
        requiredMark
      >
        <Row gutter={32} className="mt-60">
          <Col xl={6}>
            <Form.Item
              label="Campaign Type"
              name="type"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select campaign type!',
                },
              ]}
            >
              <Select
                size="large"
                placeholder="Select Type"
                onSelect={(campaignType) => {
                  fetchOfferings(
                    form.getFieldValue('company_id'),
                    campaignType
                  );
                }}
              >
                <Option value="SAMPLE" key="SAMPLE">
                  Sample
                </Option>
                <Option value="RETAIL" key="RETAIL">
                  Retail
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Campaign Name"
              name="name"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please input campaign name!',
                },
              ]}
            >
              <Input size="large" placeholder="Name" />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Select Brand"
              name="company_id"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select brand!',
                },
              ]}
            >
              <Select
                size="large"
                placeholder="Brand"
                disabled={isBrandUser}
                onChange={(companyId) => {
                  fetchOfferings(companyId, form.getFieldValue('type'));
                }}
              >
                {map(brands, (brand) => (
                  <Option value={get(brand, 'id')} key={get(brand, 'id')}>
                    {get(brand, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Select Offering"
              name="offering_id"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select offering!',
                },
              ]}
            >
              <Select size="large" placeholder="Offering">
                {map(offerings, (offering) => (
                  <Option value={get(offering, 'id')} key={get(offering, 'id')}>
                    {get(offering, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={32} className="mt-60">
          <Col xl={6}>
            <Form.Item
              label="Retailer"
              name="retailer"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select campaign type!',
                },
              ]}
            >
              <Select
                size="large"
                placeholder="Select Type"
                mode="multiple"
                onChange={(values) => {
                  setRetailers({
                    ...retailers,
                    selectedRetailers: filter(
                      get(retailers, 'list'),
                      (retailer) => includes(values, get(retailer, 'id'))
                    ),
                  });
                }}
              >
                {map(get(retailers, 'list'), (retailer) => (
                  <Option value={get(retailer, 'id')} key={get(retailer, 'id')}>
                    {get(retailer, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Location"
              name="location"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select location!',
                },
              ]}
            >
              <Cascader
                size="large"
                placeholder="Select Locations"
                multiple
                showCheckedStrategy={SHOW_CHILD}
                options={getRetailerLocation(
                  get(retailers, 'selectedRetailers')
                )}
                onChange={(values, selectedLabels) => {
                  fetchTouchPoints(map(selectedLabels, last));
                }}
              />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Touchpoints"
              name="touchpoint_ids"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select location!',
                },
              ]}
            >
              <Cascader
                size="large"
                placeholder="Vending Machines"
                multiple
                showCheckedStrategy={SHOW_CHILD}
                options={getLocationTouchPoints(get(touchPoints, 'points'))}
              />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Time Period"
              name="time_range"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select time period!',
                },
              ]}
            >
              <RangePicker size="large" format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-evenly" className="mt-60">
          <Col xl={8}>
            <Button
              className="default-primary-btn"
              type="primary"
              onClick={() => onMove(0)}
              icon={<ArrowLeftOutlined />}
            >
              Back
            </Button>
          </Col>
          <Col xl={12} />
          <Col xl={4}>
            <Button
              htmlType="submit"
              className="default-primary-btn"
              type="primary"
              block
            >
              Save & Proceed
            </Button>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default CampaignDetails;
